import React, { useRef, useEffect, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import type { DropResult } from '@hello-pangea/dnd';
import { createPortal } from 'react-dom';
import { AddBox } from '@mui/icons-material';
import { RoutePoint } from './RoutePoint';
import { LocationSearch } from '../shared/LocationSearch';
import type { PointItem } from '../../types';
import './RoutePointsList.css';

interface RoutePointsListProps {
  points: PointItem[];
  onPointDelete: (id: string) => void;
  onPointEdit?: (id: string, newPosition: { lat: number; lon: number }, displayName: string) => void;
  onPointInsert?: (insertIndex: number, newPosition: { lat: number; lon: number }, displayName: string) => void;
  onPointsReorder: (result: DropResult) => void;
  onManualGeocode?: (pointId: string, position: { lat: number; lon: number }) => void;
  geocodingLoadingPoints?: Set<string>;
  scrollToPoint?: string; // ID of point to scroll to
  deletingPointId?: string; // ID of point being deleted from external source (e.g., map)
  isExpanded?: boolean; // Whether the detail panel is in fullscreen mode
  routeSourceType?: 'manual' | 'gpx_import' | 'gpx_optimized'; // Route source type for geocoding decisions
}

export const RoutePointsList: React.FC<RoutePointsListProps> = ({
  points,
  onPointDelete,
  onPointEdit,
  onPointInsert,
  onPointsReorder,
  onManualGeocode,
  geocodingLoadingPoints,
  scrollToPoint,
  deletingPointId,
  isExpanded = false,
  routeSourceType
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pointRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const listRef = useRef<HTMLDivElement>(null);
  const [deletingPoints, setDeletingPoints] = useState<Set<string>>(new Set());
  const [insertionState, setInsertionState] = useState<{
    isOpen: boolean;
    insertIndex: number;
  }>({ isOpen: false, insertIndex: -1 });
  const [placeholderPoint, setPlaceholderPoint] = useState<PointItem | null>(null);
  const [draggedPoint, setDraggedPoint] = useState<PointItem | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hasRealMousePosition, setHasRealMousePosition] = useState(false);

  // Scroll to specific point when scrollToPoint changes
  useEffect(() => {
    if (scrollToPoint && pointRefs.current[scrollToPoint] && scrollContainerRef.current) {
      const pointElement = pointRefs.current[scrollToPoint];
      const container = scrollContainerRef.current;
      
      // Small delay to ensure the DOM has been updated
      setTimeout(() => {
        // Get the position of the point relative to the container
        const pointTop = pointElement.offsetTop;
        const containerHeight = container.clientHeight;
        const pointHeight = pointElement.clientHeight;
        
        // Calculate scroll position to center the point in view
        const scrollPosition = pointTop - (containerHeight / 2) + (pointHeight / 2);
        
        // Smooth scroll to the position
        container.scrollTo({
          top: Math.max(0, scrollPosition),
          behavior: 'smooth'
        });
      }, 50);
    }
  }, [scrollToPoint]);

    const DynamicPlusButtons: React.FC<{ points: PointItem[]; onInsert: (index: number) => void; }> = ({ points, onInsert }) => {
      const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1025;
      // Desktop keeps widened spacing; mobile reverts to original values.
      const desktopBase = 43; // matches --plus-base-offset
      const desktopStep = 58; // matches --plus-step (52px item height + 6px gap)
      const mobileBase = 43;  // same as desktop with fixed heights
      const mobileStep = 58; // same as desktop with fixed heights
      const BASE = isDesktop ? desktopBase : mobileBase;
      const STEP = isDesktop ? desktopStep : mobileStep;
      return (
        <div className="route-points-plus-overlay">
          {points.slice(0, -1).map((point, index) => (
            <button
              key={`plus-${point.id}-${points[index + 1]?.id}`}
              className="route-point-plus-button-overlay"
              onClick={() => onInsert(index)}
              style={{ '--button-top': `${BASE + index * STEP}px` } as React.CSSProperties}
              aria-label={`Insert point after position ${index + 1}`}
            >
              <AddBox />
            </button>
          ))}
        </div>
      );
    };

  // Handle external deletions (e.g., from map)
  useEffect(() => {
    if (deletingPointId && !deletingPoints.has(deletingPointId)) {
      setDeletingPoints(prev => new Set(prev).add(deletingPointId));
    }
  }, [deletingPointId, deletingPoints]);

  // Animated delete handler
  const handlePointDelete = (id: string) => {
    // Add to deleting set to trigger animation
    setDeletingPoints(prev => new Set(prev).add(id));
    
    // Call the actual delete function immediately to start route recalculation
    onPointDelete(id);
    
    // Clean up the deleting state after animation completes
    setTimeout(() => {
      setDeletingPoints(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }, 300);
  };

  // Handle plus button click
  const handlePlusClick = (insertIndex: number) => {
    // Create a placeholder point locally for visual feedback
    const actualInsertIndex = insertIndex + 1;
    const placeholderPosition = { lat: 0, lng: 0 };
    const newPlaceholder: PointItem = {
      id: `placeholder-${Date.now()}`,
      type: 'via',
      position: placeholderPosition,
      address: ''
    };
    
    // Set the placeholder and open location search
    setPlaceholderPoint(newPlaceholder);
    setInsertionState({ 
      isOpen: true, 
      insertIndex: actualInsertIndex
    });
  };

  // Handle location selection from LocationSearch
  const handleLocationSelect = (location: { lat: number; lon: number; displayName: string }) => {
    if (onPointInsert && insertionState.insertIndex >= 0) {
      onPointInsert(insertionState.insertIndex, location, location.displayName);
    }
    setPlaceholderPoint(null);
    setInsertionState({ isOpen: false, insertIndex: -1 });
  };

  // Handle LocationSearch close
  const handleLocationSearchClose = () => {
    setPlaceholderPoint(null);
    setInsertionState({ isOpen: false, insertIndex: -1 });
  };

  // Handle add endpoint button click
  const handleAddEndpoint = () => {
    const placeholderPosition = { lat: 0, lng: 0 };
    const newPlaceholder: PointItem = {
      id: `placeholder-endpoint-${Date.now()}`,
      type: 'end',
      position: placeholderPosition,
      address: ''
    };
    
    setPlaceholderPoint(newPlaceholder);
    setInsertionState({ 
      isOpen: true, 
      insertIndex: points.length
    });
  };

  // Handle add starting point button click
  const handleAddStartPoint = () => {
    const placeholderPosition = { lat: 0, lng: 0 };
    const newPlaceholder: PointItem = {
      id: `placeholder-startpoint-${Date.now()}`,
      type: 'start',
      position: placeholderPosition,
      address: ''
    };
    
    setPlaceholderPoint(newPlaceholder);
    setInsertionState({ 
      isOpen: true, 
      insertIndex: 0
    });
  };

  // Create combined list with placeholder inserted at the right position
  const getDisplayPoints = (): PointItem[] => {
    if (!placeholderPoint) return points;
    
    const insertIndex = insertionState.insertIndex;
    if (insertIndex < 0 || insertIndex > points.length) return points;
    
    const newPoints = [...points];
    newPoints.splice(insertIndex, 0, placeholderPoint);
    return newPoints;
  };

  // Handle drag start
  const handleDragStart = (start: any) => {
    const draggedItem = points.find(point => point.id === start.draggableId);
    setDraggedPoint(draggedItem || null);
    setHasRealMousePosition(false); // Reset the flag
    
    // Track mouse position for real-time updates
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setHasRealMousePosition(true);
    };
    
    const handleTouchMoveInternal = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        setMousePosition({ x: e.touches[0].clientX, y: e.touches[0].clientY });
        setHasRealMousePosition(true);
      }
    };
    
    // Don't set an initial position - wait for the first real mouse event
    // This completely eliminates flicker
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchmove', handleTouchMoveInternal, { passive: false });
    
    // Store cleanup function on the component
    (window as any).__dragCleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchmove', handleTouchMoveInternal);
    };
  };

  // Handle drag end
  const handleDragEnd = (result: DropResult) => {
    // Cleanup event listeners
    if ((window as any).__dragCleanup) {
      (window as any).__dragCleanup();
      delete (window as any).__dragCleanup;
    }
    
    setDraggedPoint(null);
    setMousePosition({ x: 0, y: 0 });
    setHasRealMousePosition(false);
    onPointsReorder(result);
  };

  const displayPoints = getDisplayPoints();
  if (points.length === 0) {
    return (
      <div className="route-points-container">
        <div className="route-points-empty">
          <svg 
            className="route-points-empty-icon" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <div className="route-points-empty-text">
            Click on the map or use the search<br />
            to add your first route point
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="route-points-container" ref={scrollContainerRef}>
      {points.length > 0 && isExpanded && (
        <button 
          className="add-startpoint-button"
          onClick={handleAddStartPoint}
          title="Add start point"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          <span>Add start point</span>
        </button>
      )}

      <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
        <Droppable droppableId="points">
          {(provided) => (
            <div
              className="route-points-droppable"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              <div className="route-points-list" ref={listRef}>
                {displayPoints.map((point, index) => {
                  // Check if this is the placeholder point
                  const isPlaceholder = point === placeholderPoint;
                  // For drag and drop, we need to exclude placeholder from draggable items
                  if (isPlaceholder) {
                    return (
                      <div key={point.id} className="route-point-wrapper">
                        <div className="route-point-item placeholder-item">
                          <RoutePoint
                            type={point.type}
                            position={point.position}
                            onDelete={() => {}}
                            onManualGeocode={onManualGeocode}
                            isGeocodingLoading={geocodingLoadingPoints?.has(point.id) || false}
                            dragHandleProps={undefined}
                            viaIndex={point.type === 'via' ? Math.max(0, index - 1) : undefined}
                            address={point.address}
                            allPoints={points}
                            pointIndex={points.indexOf(point)}
                            point={point}
                            routeSourceType={routeSourceType}
                          />
                        </div>
                      </div>
                    );
                  }
                  
                  // Regular draggable point
                  const originalIndex = points.indexOf(point);
                  return (
                    <Draggable key={point.id} draggableId={point.id} index={originalIndex}>
                      {(provided, snapshot) => (
                        <div className="route-point-wrapper">
                          <div
                            ref={(el) => {
                              provided.innerRef(el as HTMLElement | null);
                              pointRefs.current[point.id] = el as HTMLDivElement | null;
                            }}
                            {...provided.draggableProps}
                            className={`route-point-item ${point.type} ${
                              snapshot.isDragging ? 'dragging' : ''
                            } ${deletingPoints.has(point.id) || point.id === deletingPointId ? 'deleting' : ''}`}
                          >
                            <RoutePoint
                              type={point.type}
                              position={point.position}
                              onDelete={() => handlePointDelete(point.id)}
                              onEdit={onPointEdit ? (newPosition, displayName) => onPointEdit(point.id, newPosition, displayName) : undefined}
                              onManualGeocode={onManualGeocode}
                              isGeocodingLoading={geocodingLoadingPoints?.has(point.id) || false}
                              dragHandleProps={provided.dragHandleProps}
                              viaIndex={point.type === 'via' ? originalIndex - 1 : undefined}
                              address={point.address}
                              allPoints={points}
                              pointIndex={originalIndex}
                              point={point}
                              routeSourceType={routeSourceType}
                            />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
                
                {points.length > 0 && isExpanded && (
                  <button 
                    className="add-endpoint-button"
                    onClick={handleAddEndpoint}
                    title="Add end point"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                    <span>Add end point</span>
                  </button>
                )}
              </div>
              
              <DynamicPlusButtons
                points={points}
                onInsert={handlePlusClick}
              />
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Portal for drag overlay to avoid transform interference - only when actively dragging with real mouse position */}
      {draggedPoint && hasRealMousePosition && mousePosition.x > 0 && mousePosition.y > 0 && createPortal(
        <div
          style={{
            position: 'fixed',
            top: mousePosition.y - 25, // Offset to center vertically on cursor
            left: mousePosition.x - 30, // Position so drag handle is exactly under cursor
            zIndex: 10000, // Higher z-index to ensure it's on top
            pointerEvents: 'none',
            transform: 'none', // Remove rotation
            opacity: 0.9,
            boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
            transition: 'none', // No transition during drag for responsiveness
            width: 'auto', // Let it size naturally
            minWidth: '300px', // Ensure minimum width matches original
          }}
          className="drag-portal-container"
        >
          <div className={`route-point-item ${draggedPoint.type}`} style={{ 
            margin: 0, 
            transform: 'none',
            position: 'static'
          }}>
            <RoutePoint
              type={draggedPoint.type}
              position={draggedPoint.position}
              onDelete={() => {}}
              onEdit={onPointEdit ? (newPosition, displayName) => onPointEdit(draggedPoint.id, newPosition, displayName) : undefined}
              onManualGeocode={onManualGeocode}
              isGeocodingLoading={geocodingLoadingPoints?.has(draggedPoint.id) || false}
              dragHandleProps={undefined}
              viaIndex={draggedPoint.type === 'via' ? points.indexOf(draggedPoint) - 1 : undefined}
              address={draggedPoint.address}
              allPoints={points}
              pointIndex={points.indexOf(draggedPoint)}
              point={draggedPoint}
              routeSourceType={routeSourceType}
            />
          </div>
        </div>,
        document.body
      )}

      {insertionState.isOpen && (
        <LocationSearch
          isOpen={insertionState.isOpen}
          onClose={handleLocationSearchClose}
          onLocationSelect={handleLocationSelect}
          placeholder="Searching new point..."
          initialQuery=""
          className="route-point-insertion-search"
        />
      )}
    </div>
  );
};
